import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import { useMounted } from '../../hooks/useMounted';
import { usePopoverRoot } from '../../hooks/usePopoverRoot';
import { useIsRTL } from '../../locale';
import { renderInlineCssVars, useDesignTokens } from '../../theme';
import { BrowserName, getBrowserName, getOSName, OSName, setNeedSaveTextSelection } from '../../util';
import { useKeyboardShortcuts } from '../KeyboardShortcuts';
import { Menu } from '../Menu';
import cn from './ContextMenu.css.json';
import './ContextMenu.css';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const ContextMenu = props => {
  // Destructure ContextMenu props before spreding onto Menu
  const {
    style,
    className,
    suppressIndicator,
    async,
    onContextMenu: onContextMenuFromProps,
    onItemSelect,
    children,
    ...menuProps
  } = props;
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({
    x: 0,
    y: 0
  });
  const focusRef = useRef(null);
  const dt = useDesignTokens();
  const popoverRoot = usePopoverRoot();
  const mounted = useMounted();
  const {
    getActionsByKeyEvent
  } = useKeyboardShortcuts();
  const isRTL = useIsRTL();
  const onContextMenu = e => {
    // ctrl + right, return browser defalut contextmenu.
    if (e?.ctrlKey && e.button === 2) return;
    onContextMenuFromProps?.(e);
    e?.preventDefault();
    if (props.sections || async) {
      e?.stopPropagation();
    }
    focusRef.current = document.activeElement;
    const pos = e ? {
      x: e.nativeEvent.clientX,
      y: e.nativeEvent.clientY
    } : {
      dockingElement: focusRef.current
    };
    if (e?.button !== 2 && pos.x !== undefined && getBrowserName() === BrowserName.Firefox) {
      // NOVACOMMONS-3107: When open by keyboard in a dialog, FF is providing the "layout" position
      // rather than the "visual" position. Has to apply the transform of the dialog
      const dialog = focusRef.current.closest('[role=dialog]');
      if (dialog) {
        const translate = getComputedStyle(dialog).transform.match(/matrix\((.+)\)/);
        if (translate) {
          const [,,,, offsetX, offsetY] = translate[1].split(', ');
          pos.x += parseFloat(offsetX);
          pos.y += parseFloat(offsetY);
        }
      }
    }
    setPosition(pos);
    if (isOpen) {
      // NOVACOMMONS-4117
      setIsOpen(false);
      setTimeout(() => mounted.current && setIsOpen(true), 0);
    } else {
      setIsOpen(true);
    }

    // save the selected text only if it is in the event target when right click
    if (getBrowserName() === BrowserName.Firefox || getBrowserName() === BrowserName.Safari) {
      const selection = window.getSelection();
      if (e?.target && selection?.focusNode && document.activeElement?.getAttribute('contenteditable') !== 'true' && (e.target.contains(selection.focusNode) || selection.focusNode.contains(e.target) || selection.focusNode.nodeType === 3 && selection.focusNode.parentElement?.contains(e.target))) {
        setNeedSaveTextSelection(true);
      }
    }
  };
  const close = useCallback(restoreFocus => {
    setIsOpen(false);
    restoreFocus && focusRef.current?.focus?.({
      preventScroll: true
    });
  }, []);
  const onRootContextMenu = useCallback(e => {
    if (isOpen && (props.sections || async)) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, [isOpen, props.sections, async]);
  useEffect(() => {
    document.addEventListener('contextmenu', onRootContextMenu);
    return () => {
      document.removeEventListener('contextmenu', onRootContextMenu);
    };
  }, [onRootContextMenu]);
  useEffect(() => {
    if (isOpen && !suppressIndicator && (props.sections || async)) {
      focusRef.current?.classList.add(cn.contextArea);
    }
    // props.sections may be undefined in nest ContextMenu
    if (!isOpen) {
      focusRef.current?.classList.remove(cn.contextArea);
    }
  }, [isOpen, suppressIndicator, props.sections, async]);
  return /*#__PURE__*/_jsx(Fragment, {
    children: /*#__PURE__*/_jsxs("div", {
      className: cx(cn.contextContainer, className),
      style: {
        ...renderInlineCssVars({
          'context-area-outline': `${dt.tokens['color-action-border-focus']} ${dt.tokens['style-action-border-focus']} ${dt.tokens['size-border-width-s']}`,
          'context-area-outline-offset': `-${dt.tokens['size-border-width-s']}`
        }),
        ...style
      },
      onContextMenu: onContextMenu,
      onKeyDown: e => {
        const {
          actions
        } = getActionsByKeyEvent(e.nativeEvent);
        if (actions.includes('openContextMenu') && (
        // Shift+F10 will generate contextmenu event on Windows so not go this route
        getOSName() === OSName.Mac || !(e.shiftKey && e.key === 'F10'))) {
          e.preventDefault();
          if (props.sections || async) {
            e.stopPropagation();
          }
          const target = e?.target;
          const allMenuContent = popoverRoot?.querySelectorAll('[role="menu"]');
          if (allMenuContent && allMenuContent.length > 0) {
            for (const content of allMenuContent) {
              if (content && content.contains(target)) return;
            }
          }
          onContextMenu();
        }
      },
      children: [children, props.sections || async ? /*#__PURE__*/_jsx(Menu, {
        isOpen: isOpen,
        isBusy: async && !props.sections,
        ...menuProps,
        sections: props.sections ?? [],
        position: props.position ?? position,
        placement: props.placement ?? `${isRTL ? 'left' : 'right'}-start`,
        onItemSelected: (indices, selectedItemKey) => {
          if (!props.sections) return;
          onItemSelect?.(indices, selectedItemKey);
          close(true);
          props.onItemSelected?.(indices, selectedItemKey);
        },
        onDismiss: restoreFocus => {
          close(restoreFocus);
          props.onDismiss?.();
        }
      }) : null]
    })
  });
};
//# sourceMappingURL=ContextMenu.js.map